import { AUTH_EVENTS } from '@/lib/events/constants';
import { emitEvent } from '@/lib/events/helpers';
import { ApiError } from './error';
import { ERROR_CODE } from './error-code';
import { DataScopeDisabledApiError } from './error.data-scope-disabled';
import { ForbiddenApiError, type ForbiddenApiErrorAccessibles } from './error.forbidden';
import { LoginMethodDeniedError } from './error.login-method-denied';
import { NoOrganizationAccessError } from './error.no-organization-access';
import { PersonalEmailLoginDeniedError } from './error.personal-email-login-denied';
import { PersonalEmailNotAllowedError } from './error.personal-email-not-allowed';
import { ReauthRequiredError } from './error.reauth-required';
import { UnauthorizedApiError } from './error.unauthorized';
import { HTTP_STATUS } from './http-status';
import { isReauthRequiredCode } from './reauth';
import { resolveLocalizedDisplayMessage } from './resolve-display-message';
const APP_INTEGRATIONS_SETTINGS_REGEX = /^\/app-integrations\/[^/]+\/settings$/;

/** 本ハンドラが参照する認証エラー body のフィールド。 */
type AuthErrorBody = {
  code?: unknown;
  accessibles?: ForbiddenApiErrorAccessibles;
  displayMessages?: Record<string, string>;
  displayMessage?: string;
};

/** body を非破壊で JSON として読む。非 JSON / 消費済みは空オブジェクト。 */
async function parseErrorBody(response: Response): Promise<AuthErrorBody> {
  try {
    const json: unknown = await response.clone().json();
    return json !== null && typeof json === 'object' ? json as AuthErrorBody : {};
  } catch {
    return {};
  }
}

/** 外部認可サーバー / IdP リダイレクトページ（ここでの 401 はログアウトさせない）。 */
function isExternalAuthPath(): boolean {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path.startsWith('/apps/marketplace') || path.startsWith('/software/library') || APP_INTEGRATIONS_SETTINGS_REGEX.test(path) || path.startsWith('/oauth2/');
}

/**
 * 403: code / accessibles から該当エラーを throw する。
 *
 * `status` は呼び出し元が受け取った実レスポンスの status。403 でのみ呼ばれるが、
 * ボディに `statusCode` が無い応答（非 JSON など）でも status を落とさないよう、
 * リテラルではなく実際の値を受け取る。
 */
function handleForbidden(body: AuthErrorBody, status: number): never {
  if (body.code === ERROR_CODE.NO_ORGANIZATION_ACCESS) {
    throw new NoOrganizationAccessError();
  }
  if (body.code === ERROR_CODE.PERSONAL_EMAIL_NOT_ALLOWED) {
    throw new PersonalEmailNotAllowedError(resolveLocalizedDisplayMessage(body));
  }
  if (body.code === ERROR_CODE.PERSONAL_EMAIL_LOGIN_DENIED) {
    throw new PersonalEmailLoginDeniedError(resolveLocalizedDisplayMessage(body));
  }
  if (body.code === ERROR_CODE.LOGIN_METHOD_DENIED) {
    throw new LoginMethodDeniedError(resolveLocalizedDisplayMessage(body));
  }
  // 組織設定でデータ範囲が無効。ユーザーの権限スコープは足りているため代替導線ダイアログ
  // （ForbiddenApiError）ではなく、BE の displayMessages を持つ ApiError 系として扱う。
  // accessibles の有無ではなく code で判定することで、ボディの形が変わっても分岐が崩れない。
  if (body.code === ERROR_CODE.DATA_SCOPE_DISABLED) {
    throw new DataScopeDisabledApiError({
      ...body,
      statusCode: status
    });
  }
  // Guard 拒否（権限スコープ未保有。code は ACCESS_CONTROL_DENIED /
  // PERMISSION_AND_ROLE_DENIED）。accessibles は ForbiddenApiErrorDialog の代替導線に
  // 必須なので、code ではなくその存在を条件にする（無ければ下の ApiError に倒す）。
  if (body.accessibles) {
    throw new ForbiddenApiError(body.accessibles);
  }
  // 上記いずれにも当たらない 403。ApiError は statusCode が無いとパースに失敗して 500 扱いに
  // なり、呼び出し側で権限エラーと判定できず 4xx なのに Query がリトライしてしまう。ボディは
  // 非 JSON で空のこともあるため、実応答の status を明示的に載せて 403 を失わせない
  // （DC-8903）。
  throw new ApiError({
    ...body,
    statusCode: status
  });
}

/**
 * 401: 再認証要求 (AAL2_REQUIRED / MFA_REAUTH_REQUIRED) は ReauthRequiredError を
 * throw（handleApiErrorWithReauth が捕捉して再認証→再実行）、それ以外はログアウトする。
 */
async function handleUnauthorized(response: Response): Promise<void> {
  if (isExternalAuthPath()) return;
  const body = await parseErrorBody(response);
  if (isReauthRequiredCode(body.code)) {
    throw new ReauthRequiredError(resolveLocalizedDisplayMessage(body));
  }
  emitEvent(AUTH_EVENTS.UNAUTHORIZED, {
    response
  });
  throw new UnauthorizedApiError();
}

/**
 * 401/403 レスポンスを解釈して該当エラーを throw する（不要な 401 は何もしない）。
 * openapi-fetch ミドルウェアと oRPC authFetch で共有する。
 */
export async function handleAuthErrorResponse(response: Response): Promise<void> {
  if (response.status === HTTP_STATUS.FORBIDDEN) {
    handleForbidden(await parseErrorBody(response), response.status);
  }
  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    await handleUnauthorized(response);
  }
}